import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, Sun, Moon, MapPin, LogOut, Truck, Store, Users, Award, Heart, Gift, HelpCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useAuthStore, useCartStore, useThemeStore } from '@/store';
import VoiceSearch from '@/components/VoiceSearch';
import NotificationBell from '@/components/NotificationBell';

import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function Navbar() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { itemCount } = useCartStore();
  const { theme, toggleTheme } = useThemeStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <img 
              src={ASTERPOS_LOGO} 
              alt="Chain29" 
              className="h-12 w-auto rounded-xl asterpos-logo transition-transform hover:scale-105"
              style={{
                filter: theme === 'dark' ? 'drop-shadow(0 0 8px rgba(251, 146, 60, 0.3))' : 'none'
              }}
            />
            <span className="text-xl font-bold font-['Outfit'] gradient-text">
              Chain29
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="nav-home"
            >
              Restaurants
            </Link>
            {isAuthenticated && (
              <>
                <Link 
                  to="/orders" 
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="nav-orders"
                >
                  Orders
                </Link>
                <Link 
                  to="/group-order" 
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  data-testid="nav-group"
                >
                  <Users className="h-4 w-4" /> Group Order
                </Link>
                <Link 
                  to="/loyalty" 
                  className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  data-testid="nav-loyalty"
                >
                  <Award className="h-4 w-4" /> Rewards
                </Link>
              </>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2">
            {/* AI Voice Search */}
            <VoiceSearch />
            
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            {/* Cart Button */}
            <Button
              variant="ghost"
              size="icon"
              className="relative rounded-full"
              onClick={() => {
                const event = new CustomEvent('toggleCart');
                window.dispatchEvent(event);
              }}
              data-testid="cart-button"
            >
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium cart-badge-animate">
                  {itemCount}
                </span>
              )}
            </Button>

            {/* Notifications */}
            <NotificationBell />

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.full_name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="menu-orders">
                    <MapPin className="mr-2 h-4 w-4" />
                    My Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/loyalty')} data-testid="menu-loyalty">
                    <Award className="mr-2 h-4 w-4" />
                    Rewards
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                    <Heart className="mr-2 h-4 w-4" />
                    Favorites
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/addresses')} data-testid="menu-addresses">
                    <MapPin className="mr-2 h-4 w-4" />
                    Addresses
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/referral')} data-testid="menu-referral">
                    <Gift className="mr-2 h-4 w-4" />
                    Invite Friends
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/group-order')} data-testid="menu-group">
                    <Users className="mr-2 h-4 w-4" />
                    Group Order
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/help')} data-testid="menu-help">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Help & Support
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="btn-pill bg-primary hover:bg-primary/90"
                data-testid="login-button"
              >
                Sign In
              </Button>
            )}

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="rounded-full">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="flex flex-col gap-4 mt-8">
                  <Link 
                    to="/" 
                    className="text-lg font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Restaurants
                  </Link>
                  {isAuthenticated && (
                    <>
                      <Link 
                        to="/orders" 
                        className="text-lg font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        My Orders
                      </Link>
                      <Link 
                        to="/group-order" 
                        className="text-lg font-medium flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Users className="h-5 w-5" /> Group Order
                      </Link>
                      <Link 
                        to="/loyalty" 
                        className="text-lg font-medium flex items-center gap-2"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Award className="h-5 w-5" /> Rewards
                      </Link>
                      <Link 
                        to="/profile" 
                        className="text-lg font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Profile
                      </Link>
                    </>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
